import { useMutation, useQuery } from '@tanstack/react-query';
import { remixApi } from '@tectonic/api-client';
import { getErrorMessage } from '@tectonic/errors';
import {
  getCountries,
  requestOtpForUserUpdate,
  updateUser,
} from '@tectonic/remix-client-network';
import { ElemasonAuthStep } from '@tectonic/types';
import { delay } from '@tectonic/utils';
import { isEmpty } from 'lodash-es';
import { useEffect, useRef } from 'react';
import { useElemasonContext } from '../../../contexts';
import { useToast } from '../../../core/ElemasonEntry/Toast';
import { queryKeys } from '../../../queryKeys';
import { useAuthStore } from '../../../store';
import { isVerificationRequired, toPhoneText } from '../../../utils';

import type {
  Country,
  ElemasonAuthProfileUpdateFormWidget,
  UserUpdateOtpRequestPayload,
} from '@tectonic/types';
import type { ProfileEditFormValue } from '../../../components';

const countriesQueryFn = async (): Promise<Country[]> => {
  const response = await getCountries();
  if (response.error || isEmpty(response.data)) {
    throw response.error;
  }
  return response.data!;
};

const requestOtpMutationFn = async (payload: UserUpdateOtpRequestPayload) => {
  const response = await requestOtpForUserUpdate(payload);
  if (response.error) {
    throw response.error;
  }
};

const updateProfileMutationFn = async (payload: ProfileEditFormValue) => {
  const { phoneNumber, ...rest } = payload;
  const phone = toPhoneText(phoneNumber)!;
  const response = await updateUser({ ...rest, phone });

  if (response.error) {
    throw response.error;
  }

  return response.data;
};

const useProfileUpdateForm = (widget: ElemasonAuthProfileUpdateFormWidget) => {
  const wData = widget.data!;
  const { searchParams } = useElemasonContext();
  const isSearchParamReferrerCodeApplied = useRef<boolean>(false);
  const setStep = useAuthStore((state) => state.setStep);

  const { showToast } = useToast();

  const {
    data: user,
    isError: hasUserLoadError,
    isLoading: isUserLoading,
  } = useQuery({
    queryKey: queryKeys.user(),
    queryFn: () => remixApi.geCurrentUser(),
  });

  const {
    isError: hasApplyReferrerCodeError,
    isPending: isApplyingReferrerCode,
    isSuccess: isApplyingReferrerCodeSuccess,
    mutateAsync: applyReferrerCode,
  } = useMutation({
    mutationFn: (referrerCode: string) =>
      remixApi.applyReferrerCode({ referrerCode }),
  });

  const {
    isLoading: isCountriesLoading,
    isError: hasLoadError,
    data: countries,
  } = useQuery({ queryKey: queryKeys.countries(), queryFn: countriesQueryFn });

  const { isPending: isRequestingOtp, mutateAsync: requestOtp } = useMutation({
    mutationFn: requestOtpMutationFn,
    mutationKey: [],
  });

  const { isPending: isUpdatingProfile, mutateAsync: updateProfile } =
    useMutation({
      mutationFn: updateProfileMutationFn,
      mutationKey: [],
    });

  useEffect(() => {
    if (isSearchParamReferrerCodeApplied.current || !searchParams.referrerCode)
      return;

    applyReferrerCode(searchParams.referrerCode);
    isSearchParamReferrerCodeApplied.current = true;
  }, [isSearchParamReferrerCodeApplied, searchParams.referrerCode]);

  const isLoading =
    isRequestingOtp ||
    isUpdatingProfile ||
    isUserLoading ||
    isApplyingReferrerCode;

  const onSubmit = async (
    value: ProfileEditFormValue,
    referrerCode?: string
  ) => {
    try {
      if (referrerCode && !user?.referrerCode)
        await applyReferrerCode(referrerCode);
      const { phoneNumber } = value;
      const phone = toPhoneText(phoneNumber)!;
      if (isVerificationRequired(phoneNumber, user!)) {
        await requestOtp({ phone });
        setStep(ElemasonAuthStep.PROFILE_DETAILS_OTP_CONFIRMATION, {
          profileUpdates: value,
        });
        return;
      }
      await updateProfile(value);
      showToast({ title: wData.message.profileUpdateSuccess });
      await delay(1000);
      globalThis.location.reload();
    } catch (error) {
      showToast({ title: getErrorMessage(error, wData.errorMessages) });
    }
  };

  return {
    user,
    onSubmit,
    countries,
    hasLoadError,
    isUserLoading,
    hasUserLoadError,
    isCountriesLoading,
    isApplyingReferrerCode,
    isSubmitting: isLoading,
    hasApplyReferrerCodeError,
    isApplyingReferrerCodeSuccess,
    onApplyReferrerCode: applyReferrerCode,
    defaultReferrerCode: searchParams.referrerCode,
  };
};

export { useProfileUpdateForm };
